import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { AgentBackLink } from "@/pages/agent/components/agent-back-link";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { AgentTaskDetailCard } from "@/pages/agent/task/agent-task-detail-card";
import { AgentTaskMetaCard } from "@/pages/agent/task/agent-task-meta-card";
import { buildTaskDetailViewModel } from "@/pages/agent/task/agent-task-page.utils";
import { showNotImplementedAlert } from "@/pages/agent/utils/agent-not-implemented";
import { useAppStore } from "@/store";
import type { AssistantTaskObservable } from "@/store/assistant";

interface AgentTaskPageProps {
  taskId: string;
}

export const AgentTaskPage = observer(({ taskId }: AgentTaskPageProps) => {
  const { store } = useAppStore();
  const task = store.assistantTasks.getAssistantTaskObservableById({ assistantTaskId: taskId });

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Tasks}>
      <AgentBackLink
        label="Back to Tasks"
        onClick={() => {
          store.agentOverlay.openTasks();
        }}
      />

      {task ? (
        <TaskDetail task={task} />
      ) : (
        <StyledNotFound color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Task not found.
        </StyledNotFound>
      )}
    </AgentPageLayout>
  );
});

const TaskDetail = observer(({ task }: { task: AssistantTaskObservable }) => {
  const { store } = useAppStore();
  const taskDetail = buildTaskDetailViewModel({
    task,
    projects: store.assistantProjects.all,
    followUps: store.assistantFollowUps.all,
  });
  const openProject = ({ projectId }: { projectId: string }) => {
    store.agentOverlay.openProject({ projectId });
  };

  return (
    <StyledDetailGrid>
      <AgentTaskDetailCard
        taskDetail={taskDetail}
        onCommentTask={showNotImplementedAlert}
        onOpenProject={openProject}
        onOpenNextFollowUp={showNotImplementedAlert}
      />
      <AgentTaskMetaCard taskDetail={taskDetail} onOpenProject={openProject} />
    </StyledDetailGrid>
  );
});

const StyledDetailGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[5]};
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StyledNotFound = styled(MDSText)`
  padding: ${({ theme }) => theme.spacing[6]} 0;
`;
